import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Chess, DEFAULT_POSITION} from "chess.js";
import socket from "../../utils/clientSocket";
import {gatherRookContext} from "../../utils/rookContext";
import {parseRookText} from "../../utils/rookText";
import RookAvatar, {RookFace} from "./RookAvatar";
import Icon from "../ui/Icon";

// Conversations are kept per board while you move around the site (not saved anywhere)
const conversations = new Map();
// Rook thinks before he answers, which can take a while on hard questions
const ANSWER_TIMEOUT_MS = 90000;

let nextId = 1;
const newId = () => `${Date.now().toString(36)}-${nextId++}`;

const HINT_LABELS = ["Hint", "Bigger hint", "Show the move"];

/**
 * Rook's private chat.
 *  - position: {startFen, moves: [uci]} the position on the board the questions are about
 *  - mode: "practice" | "computer" | "analysis"
 *  - playerColor: "w" | "b" when the player has one side
 *  - getEngine: () => the browser engine to use for the briefing
 *  - preview: the useLinePreview() object of the board next to the chat
 *  - storeKey: conversations with the same key are shared (e.g. one per game)
 */
const RookChat = ({position, mode = "practice", playerColor, getEngine, preview, storeKey = "practice", className = ""}) => {
    const [messages, setMessagesState] = useState(() => conversations.get(storeKey) || []);
    const [input, setInput] = useState("");
    const [mood, setMood] = useState("idle");
    const [usage, setUsage] = useState(null);
    const pending = useRef(null);       // {id, timer}
    const listRef = useRef(null);
    const stickToBottom = useRef(true);
    const hint = useRef({fen: null, level: 0});
    const moodTimer = useRef(null);

    const setMessages = useCallback((update) => {
        setMessagesState((prev) => {
            const next = typeof update === "function" ? update(prev) : update;
            conversations.set(storeKey, next);
            return next;
        });
    }, [storeKey]);

    // The conversation follows the board it belongs to
    useEffect(() => {
        setMessagesState(conversations.get(storeKey) || []);
    }, [storeKey]);

    const {fen, lastSan, isOver} = useMemo(() => {
        const chess = new Chess(position?.startFen || DEFAULT_POSITION);
        let last = null;
        for (const uci of position?.moves || []) {
            try {
                last = chess.move({from: uci.slice(0, 2), to: uci.slice(2, 4), promotion: uci[4]}).san;
            } catch (e) {
                break;
            }
        }
        return {fen: chess.fen(), lastSan: last, isOver: chess.isGameOver()};
    }, [position]);

    const busy = messages.some((m) => m.streaming);

    const flashMood = (next, ms) => {
        clearTimeout(moodTimer.current);
        setMood(next);
        moodTimer.current = setTimeout(() => setMood("idle"), ms);
    };

    const finish = useCallback((id, patch) => {
        if (pending.current?.id === id) {
            clearTimeout(pending.current.timer);
            pending.current = null;
        }
        setMessages((prev) => prev.map((m) => (m.id === id ? {...m, streaming: false, ...patch} : m)));
    }, [setMessages]);

    // Answers arrive over the socket in small pieces
    useEffect(() => {
        const onDelta = ({id, text}) => {
            if (pending.current?.id !== id) return;
            setMood("talking");
            setMessages((prev) => prev.map((m) => (m.id === id ? {...m, text: m.text + text, phase: "answer"} : m)));
        };
        const onDone = ({id, remaining, limit, stopped, offline}) => {
            if (pending.current?.id !== id) return;
            setUsage((u) => ({...(u || {}), remaining, limit}));
            finish(id, {stopped: !!stopped, offline: !!offline});
            setMood("idle");
        };
        const onError = ({id, message}) => {
            if (pending.current?.id !== id) return;
            finish(id, {error: message});
            flashMood("puzzled", 2400);
        };
        const onUsage = (data) => setUsage(data);
        socket.on("rookDelta", onDelta);
        socket.on("rookDone", onDone);
        socket.on("rookError", onError);
        socket.on("rookUsage", onUsage);
        socket.emit("rookUsage");
        return () => {
            socket.off("rookDelta", onDelta);
            socket.off("rookDone", onDone);
            socket.off("rookError", onError);
            socket.off("rookUsage", onUsage);
            clearTimeout(moodTimer.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [finish, setMessages]);

    // Keep the newest text in view, unless the player scrolled up to read
    useEffect(() => {
        const list = listRef.current;
        if (list && stickToBottom.current) list.scrollTop = list.scrollHeight;
    }, [messages]);
    const onScroll = () => {
        const list = listRef.current;
        if (list) stickToBottom.current = list.scrollHeight - list.scrollTop - list.clientHeight < 40;
    };

    const ask = async (text, {hintLevel = 0} = {}) => {
        const question = text.trim();
        if (!question || busy) return;
        const id = newId();
        const askedAt = {fen, startFen: position?.startFen || DEFAULT_POSITION, moves: [...(position?.moves || [])]};
        const history = messages
            .filter((m) => !m.error && m.text && !m.stopped)
            .map((m) => ({role: m.role, text: m.text}));
        stickToBottom.current = true;
        setMessages((prev) => [
            ...prev,
            {id: `${id}-q`, role: "user", text: question},
            {id, role: "rook", text: "", fen, streaming: true, phase: "reading"}
        ]);
        setInput("");
        setMood("thinking");

        let context;
        try {
            const engine = getEngine ? getEngine() : null;
            context = await gatherRookContext(engine, askedAt, {mode, playerColor, hintLevel, question});
        } catch (e) {
            // Rook can still answer without the engine, just with less to go on
            context = {startFen: askedAt.startFen, moves: askedAt.moves, mode, playerColor, hintLevel, engine: {lines: []}};
        }
        if (!socket.connected) {
            setMessages((prev) => prev.map((m) => (m.id === id ? {...m, streaming: false, error: "You are offline. Try again when the connection is back."} : m)));
            flashMood("puzzled", 2000);
            return;
        }
        pending.current = {
            id,
            timer: setTimeout(() => {
                finish(id, {error: "Rook did not answer in time. Try asking again."});
                flashMood("puzzled", 2000);
            }, ANSWER_TIMEOUT_MS)
        };
        socket.emit("rookAsk", {id, conversation: [...history, {role: "user", text: question}], context});
    };

    const stop = () => {
        if (pending.current) socket.emit("rookStop", {id: pending.current.id});
    };

    const askHint = () => {
        if (hint.current.fen !== fen) hint.current = {fen, level: 0};
        hint.current.level = Math.min(3, hint.current.level + 1);
        const level = hint.current.level;
        ask(["Can I have a hint?", "Can I have a bigger hint?", "Just show me the move, please."][level - 1], {hintLevel: level});
    };
    const hintLabel = HINT_LABELS[hint.current.fen === fen ? Math.min(2, hint.current.level) : 0];

    const quick = [
        {label: "Threats", text: "What is the threat here? What should I watch out for?", show: !isOver},
        {label: "Plan", text: "What's a good plan in this position?", show: !isOver},
        {label: lastSan ? `Was ${lastSan} good?` : null, text: `Was ${lastSan} a good move? If not, what was better and why?`, show: !!lastSan},
        {label: hintLabel, onClick: askHint, show: !isOver}
    ].filter((q) => q.show && q.label);

    const clear = () => {
        if (busy) return;
        setMessages([]);
        preview?.close();
    };

    const isRookTurnToShow = (key) => preview?.key === key;
    const renderPieces = (message) => {
        const paragraphs = parseRookText(message.text, message.fen, message.streaming);
        return paragraphs.map((pieces, pi) => (
            <p key={pi}>
                {pieces.map((piece, si) => {
                    const key = `${message.id}:${pi}:${si}`;
                    const content = (() => {
                        if (piece.type === "square") {
                            return (
                                <button type="button" className="rook-square" onClick={() => preview?.highlight(piece.square)}
                                        title={`Show ${piece.square} on the board`}>
                                    {piece.square}
                                </button>
                            );
                        }
                        if (piece.type === "line") {
                            const single = piece.line.moves.length === 1;
                            return (
                                <button type="button"
                                        className={`rook-line ${single ? "rook-line--single" : ""} ${piece.line.threat ? "rook-line--threat" : ""} ${isRookTurnToShow(key) ? "active" : ""}`}
                                        onClick={() => (isRookTurnToShow(key) ? preview?.close() : preview?.show(piece.line, key))}
                                        title={isRookTurnToShow(key) ? "Back to the position" : "Show on the board"}>
                                    <Icon name={isRookTurnToShow(key) ? "close" : "play"} size={12}/>
                                    {single ? piece.line.moves[0].san : piece.line.label}
                                </button>
                            );
                        }
                        return piece.text;
                    })();
                    return piece.bold ? <strong key={si}>{content}</strong> : <React.Fragment key={si}>{content}</React.Fragment>;
                })}
            </p>
        ));
    };

    const status = busy ? (mood === "talking" ? "Answering…" : "Looking at the board…") : "Your chess coach";

    return (
        <div className={`rook-chat ${className}`}>
            <div className="rook-chat__bar">
                <RookAvatar mood={mood} className="rook-robin--peek" onClick={() => !busy && flashMood("happy", 1300)}/>
                <div className="rook-chat__who">
                    <div className="rook-chat__name">Rook</div>
                    <div className="rook-chat__status">{status}</div>
                </div>
                {messages.length > 0 && (
                    <button type="button" className="icon-btn icon-btn--sm" onClick={clear} disabled={busy}
                            aria-label="Start a new conversation" title="New conversation">
                        <Icon name="refresh"/>
                    </button>
                )}
            </div>

            <div className="rook-chat__list" ref={listRef} onScroll={onScroll}>
                {messages.length === 0 && (
                    <div className="rook-chat__intro">
                        <p><strong>Hi, I'm Rook!</strong> Ask me anything about the position on the board. I can point out threats,
                            suggest plans and show you whole lines, move by move.</p>
                        <p className="small muted">I only see this board, and only you can see our chat.</p>
                    </div>
                )}
                {messages.map((m) => m.role === "user" ? (
                    <div key={m.id} className="rook-msg rook-msg--me"><div className="rook-msg__bubble">{m.text}</div></div>
                ) : (
                    <div key={m.id} className="rook-msg rook-msg--rook">
                        <RookFace size={28}/>
                        <div className="rook-msg__bubble">
                            {m.text ? renderPieces(m) : null}
                            {m.streaming && !m.text && <span className="rook-typing" aria-label="Rook is thinking"><i/><i/><i/></span>}
                            {m.error && <p className="rook-msg__error">{m.error}</p>}
                            {m.stopped && <p className="small muted">(stopped)</p>}
                        </div>
                    </div>
                ))}
            </div>

            <div className="rook-chat__quick">
                {quick.map((q) => (
                    <button key={q.label} type="button" className="rook-chip" disabled={busy}
                            onClick={q.onClick || (() => ask(q.text))}>
                        {q.label}
                    </button>
                ))}
            </div>
            <form className="rook-chat__form" onSubmit={(e) => {
                e.preventDefault();
                ask(input);
            }}>
                <input className="input" value={input} onChange={(e) => setInput(e.target.value)} maxLength={500}
                       placeholder="Ask Rook about the position…" aria-label="Ask Rook"/>
                {busy ? (
                    <button type="button" className="icon-btn" onClick={stop} aria-label="Stop the answer" title="Stop">
                        <Icon name="stop"/>
                    </button>
                ) : (
                    <button type="submit" className="icon-btn" disabled={!input.trim()} aria-label="Send" title="Send">
                        <Icon name="send"/>
                    </button>
                )}
            </form>
            {usage && usage.remaining !== undefined && (
                <div className="rook-chat__usage">
                    {usage.remaining > 0
                        ? `${usage.remaining} question${usage.remaining === 1 ? "" : "s"} left today${usage.isGuest ? ". Log in for more" : ""}`
                        : "No questions left today"}
                </div>
            )}
        </div>
    );
};

export default RookChat;
